import * as React from "react";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import {
  Typography,
  Box,
  CircularProgress,
  Tooltip,
  Grid,
  TextField,
  InputAdornment,
} from "@mui/material";
import { connect } from "react-redux";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PlaceIcon from "@mui/icons-material/Place";
import { centered_flex_box } from "../../app/components/Styles";
import { useNavigate } from "react-router-dom";
import { submitEventRequest } from "../../app/store/actions/userActions";
import { MarketingFormPage, MarketingButton } from "../../app/components/marketing";
import {
  marketingColors,
  marketingFonts,
  marketingFormFieldSx,
  marketingFormHighlightBoxSx,
} from "../../app/components/tickitMarketingTheme";

export const RequestEvent = ({ submitEventRequest, isLoading }) => {
  window.Buffer = window.Buffer || require("buffer").Buffer;

  const navigate = useNavigate();

  const handleSubmit = async (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    try {
      const creation = {
        title: data.get("title"),
        description: data.get("description"),
        averagePrice: data.get("averagePrice"),
        date: data.get("date"),
        location: data.get("location"),
        capacity: data.get("capacity"),
        public: data.get("public") === "on",
        needsVerification: false,
        byRequest: data.get("byRequest") === "on",
        gatesOpenAt: data.get("gatesOpenAt"),
        locationURL: data.get("locationURL"),
        oraganizer: data.get("oraganizer"),
      };

      submitEventRequest({ creation: creation }, navigate);
    } catch (err) {
      console.log(err);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ ...centered_flex_box, minHeight: "100vh" }}>
        <CircularProgress sx={{ color: marketingColors.p700 }} />
      </Box>
    );
  }

  return (
    <MarketingFormPage
      title="Get powered by Tickit"
      subtitle="Tell us about your wedding, corporate event, or ticketed experience. Our team reviews requests and helps you launch ticketing and invitations on Tickit."
    >
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              id="title"
              label="Event title"
              name="title"
              placeholder="e.g. Nour & Karim — Wedding reception"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EventAvailableIcon sx={{ color: marketingColors.p400, fontSize: 22 }} />
                  </InputAdornment>
                ),
              }}
              sx={marketingFormFieldSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              multiline
              minRows={4}
              name="description"
              label="Event description"
              id="description"
              placeholder="What should guests know? Dress code, agenda, ticketing notes…"
              sx={marketingFormFieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              type="number"
              id="capacity"
              label="Expected guest capacity"
              name="capacity"
              sx={marketingFormFieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              id="averagePrice"
              label="Average ticket price (EGP)"
              name="averagePrice"
              sx={marketingFormFieldSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="oraganizer"
              label="Organizer or planner name"
              name="oraganizer"
              sx={marketingFormFieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              type="datetime-local"
              id="date"
              label="Event date & time"
              name="date"
              InputLabelProps={{ shrink: true }}
              sx={marketingFormFieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              type="datetime-local"
              id="gatesOpenAt"
              label="Gates open"
              name="gatesOpenAt"
              InputLabelProps={{ shrink: true }}
              sx={marketingFormFieldSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              fullWidth
              id="location"
              label="Venue / location"
              name="location"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PlaceIcon sx={{ color: marketingColors.p400, fontSize: 22 }} />
                  </InputAdornment>
                ),
              }}
              sx={marketingFormFieldSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="url"
              id="locationURL"
              label="Google Maps link"
              name="locationURL"
              placeholder="https://maps.google.com/..."
              sx={marketingFormFieldSx}
            />
          </Grid>
        </Grid>

        <Box sx={{ ...marketingFormHighlightBoxSx, mt: 3 }}>
          <Typography sx={{ fontFamily: marketingFonts.display, fontWeight: 800, fontSize: "0.95rem", mb: 1.5, color: marketingColors.p800 }}>
            Visibility & approval
          </Typography>
          <FormControlLabel
            control={<Checkbox name="public" id="public" sx={{ color: marketingColors.p500 }} />}
            label={<Typography sx={{ fontFamily: marketingFonts.body, fontSize: "0.9rem", color: marketingColors.gray700 }}>List as a public event</Typography>}
          />
          <Tooltip title="Guests must be approved before receiving a ticket">
            <FormControlLabel
              control={<Checkbox name="byRequest" id="byRequest" sx={{ color: marketingColors.p500 }} />}
              label={<Typography sx={{ fontFamily: marketingFonts.body, fontSize: "0.9rem", color: marketingColors.gray700 }}>Require organizer approval per guest</Typography>}
            />
          </Tooltip>
        </Box>

        <MarketingButton type="submit" fullWidth size="large" sx={{ mt: 3, py: 1.75, fontSize: "1rem" }}>
          Submit request
        </MarketingButton>
        <Typography
          sx={{
            mt: 2,
            textAlign: "center",
            fontFamily: marketingFonts.body,
            fontSize: "0.8rem",
            color: marketingColors.gray500,
          }}
        >
          We will reach out using the details you provide in your account where applicable.
        </Typography>
      </Box>
    </MarketingFormPage>
  );
};

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  token: state?.auth?.token,
  isLoading: state?.events?.isLoading,
});

const mapDispatchToProps = { submitEventRequest };

export default connect(mapStateToProps, mapDispatchToProps)(RequestEvent);
